import { useMemo, useState, type CSSProperties } from 'react';
import { Search as SearchIcon, Spline, Tag, Zap, X } from 'lucide-react';
import { useStore } from '../store';
import { computeNets, type Net, type NetSource } from '../nets';

const SOURCE_META: Record<NetSource, { Icon: typeof Zap; color: string; label: string }> = {
  power: { Icon: Zap, color: 'var(--accent)', label: 'power' },
  label: { Icon: Tag, color: 'var(--selection)', label: 'label' },
  auto: { Icon: Spline, color: 'var(--text-muted)', label: 'auto' },
};

export function NetsModal({ onClose }: { onClose: () => void }) {
  const sheets = useStore((s) => s.sheets);
  const focusNet = useStore((s) => s.focusNet);
  const focusedNetMode = useStore((s) => s.focusedNetMode);
  const setFocusedNetMode = useStore((s) => s.setFocusedNetMode);
  const setActiveSheet = useStore((s) => s.setActiveSheet);

  const nets = useMemo(() => computeNets(sheets), [sheets]);
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const visible = query
    ? nets.filter(
        (n) =>
          n.name.toLowerCase().includes(query) ||
          n.pins.some((p) => `${p.component} ${p.pinName}`.toLowerCase().includes(query)),
      )
    : nets;

  const focus = (net: Net) => {
    focusNet({ id: net.id, edgeIds: net.edgeIds, nodeIds: net.nodeIds });
    const sheetId = net.sheetIds[0];
    if (sheetId) {
      setActiveSheet(sheetId);
      window.setTimeout(
        () => window.dispatchEvent(new CustomEvent('skema:focus', { detail: { nodeIds: net.nodeIds } })),
        160,
      );
    }
    onClose();
  };

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={titleBarStyle}>
          <span style={titleStyle}>Nets</span>
          <button style={closeBtnStyle} onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>

        <div style={searchRowStyle}>
          <div style={{ position: 'relative', flex: 1 }}>
            <SearchIcon size={13} style={searchIconStyle} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search nets / pins…"
              style={searchInputStyle}
            />
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {nets.length} net{nets.length === 1 ? '' : 's'}
          </span>
        </div>

        <div style={modeRowStyle}>
          <span style={modeLabelStyle}>Focus mode</span>
          <button
            style={{
              ...modeButtonStyle,
              ...(focusedNetMode === 'dim' ? activeModeButtonStyle : null),
            }}
            onClick={() => setFocusedNetMode('dim')}
          >
            Dim others
          </button>
          <button
            style={{
              ...modeButtonStyle,
              ...(focusedNetMode === 'solo' ? activeModeButtonStyle : null),
            }}
            onClick={() => setFocusedNetMode('solo')}
          >
            Dim more
          </button>
        </div>

        <div style={listStyle}>
          {nets.length === 0 ? (
            <div style={emptyStyle}>
              No nets yet. Wire component pins together (and drop GND/VCC power symbols
              or net labels) on a schematic sheet.
            </div>
          ) : (
            visible.map((net) => {
              const m = SOURCE_META[net.source];
              const conflict = net.labelNames.length > 1;
              return (
                <div key={net.id} style={rowStyle} title="Electrical net">
                  <m.Icon size={15} style={{ color: m.color, flexShrink: 0, marginTop: 1 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={rowNameStyle}>
                      {net.name}
                      {net.global && <span style={badgeStyle}>global</span>}
                      {conflict && <span style={{ ...badgeStyle, ...conflictBadgeStyle }}>conflict</span>}
                    </span>
                    <span style={rowMetaStyle}>
                      {net.pins.length} pin{net.pins.length === 1 ? '' : 's'} · {m.label}
                      {net.sheetIds.length > 1 ? ` · ${net.sheetIds.length} sheets` : ''}
                      {net.pins.length > 0
                        ? ` — ${net.pins
                            .slice(0, 3)
                            .map((p) => `${p.component}.${p.pinName}`)
                            .join(', ')}${net.pins.length > 3 ? '…' : ''}`
                        : ''}
                    </span>
                  </span>
                  <div style={rowActionsStyle}>
                    <button style={smallActionStyle} onClick={() => focus(net)}>
                      Focus
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const panelStyle: CSSProperties = {
  width: 560,
  maxWidth: '92vw',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
};
const titleBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px 8px 14px',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
};
const titleStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
};
const closeBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 22,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};
const searchRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'var(--panel-alt)',
};
const modeRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'var(--panel)',
};
const modeLabelStyle: CSSProperties = {
  marginRight: 4,
  fontSize: 10,
  fontWeight: 800,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  letterSpacing: 0.5,
};
const modeButtonStyle: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel-alt)',
  color: 'var(--text-muted)',
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};
const activeModeButtonStyle: CSSProperties = {
  borderColor: 'var(--accent)',
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
};
const searchIconStyle: CSSProperties = {
  position: 'absolute',
  left: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  pointerEvents: 'none',
};
const searchInputStyle: CSSProperties = {
  width: '100%',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 8px 5px 26px',
  fontSize: 12,
  background: 'var(--panel)',
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};
const listStyle: CSSProperties = { overflowY: 'auto', padding: 6, minHeight: 120 };
const emptyStyle: CSSProperties = {
  padding: '36px 18px',
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 9,
  width: '100%',
  textAlign: 'left',
  padding: '7px 9px',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text)',
  cursor: 'pointer',
};
const rowActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginLeft: 8,
  flexShrink: 0,
};
const smallActionStyle: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  padding: '4px 7px',
  fontSize: 10.5,
  fontWeight: 700,
  cursor: 'pointer',
};
const rowNameStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12.5,
  fontWeight: 800,
  fontFamily: 'ui-monospace, Consolas, monospace',
};
const rowMetaStyle: CSSProperties = {
  display: 'block',
  fontSize: 10.5,
  color: 'var(--text-muted)',
  marginTop: 2,
};
const badgeStyle: CSSProperties = {
  fontSize: 8.5,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  padding: '1px 5px',
  borderRadius: 999,
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
  fontFamily: 'system-ui, sans-serif',
};
const conflictBadgeStyle: CSSProperties = {
  background: 'var(--danger-soft)',
  color: 'var(--danger)',
};
